import { Renderer } from "../pipeline/renderer"
import { Texture } from "../backends/webgl/texture"
import { vec2 } from "gl-matrix"
import { BrushStroke, calcBoundingBox } from "./brushing"
import { Rectangle } from "../types"

export interface BrushMaskCacheEntry {
	texture: Texture
	strokes: BrushStroke[]
}

export class MaskPainter {
	private pointsBuffer: WebGLBuffer | null
	private localCoordsBuffer: WebGLBuffer | null
	private quadVertexBuffer: WebGLBuffer | null
	private localCoords = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1])
	private renderer: Renderer
	private forHealing: boolean

	constructor(renderer: Renderer, forHealing: boolean) {
		this.renderer = renderer
		const { gl } = renderer.backend

		this.quadVertexBuffer = gl.createBuffer()
		this.pointsBuffer = gl.createBuffer()
		this.localCoordsBuffer = gl.createBuffer()

		this.forHealing = forHealing
	}

	private createQuadVertices(
		points: number[],
		brushSize: number,
		aspectRatio: number,
		rect: Rectangle,
	): [Float32Array, Float32Array] {
		const instancePositions: number[] = []

		const [offsetX, offsetY] = [rect.x, rect.y]
		const [scaleX, scaleY] = [1.0 / rect.width, 1.0 / rect.height]

		let halfBrushSizeX = aspectRatio > 1 ? brushSize * 0.5 : brushSize * 0.5 * aspectRatio
		let halfBrushSizeY = aspectRatio > 1 ? (brushSize * 0.5) / aspectRatio : brushSize * 0.5

		halfBrushSizeX *= scaleX
		halfBrushSizeY *= scaleY

		const vertices = new Float32Array([
			-halfBrushSizeX,
			-halfBrushSizeY,
			halfBrushSizeX,
			-halfBrushSizeY,
			-halfBrushSizeX,
			halfBrushSizeY,
			halfBrushSizeX,
			halfBrushSizeY,
		])

		for (let i = 0; i < points.length; i += 2) {
			const cx = points[i]
			const cy = points[i + 1]

			if (!cx || !cy) {
				continue
			}

			instancePositions.push((cx - offsetX) * scaleX, (cy - offsetY) * scaleY)
		}

		return [vertices, new Float32Array(instancePositions)]
	}

	private drawRect(xRange: vec2, yRange: vec2) {
		const renderer = this.renderer
		const { gl, shaders } = renderer.backend

		const [x0, x1, y0, y1] = [
			2.0 * xRange[0] - 1.0,
			2.0 * xRange[1] - 1.0,
			2.0 * yRange[0] - 1.0,
			2.0 * yRange[1] - 1.0,
		]

		const vertices = new Float32Array([x0, y0, x1, y0, x1, y1, x0, y1])

		const positionAttributeLocation = shaders.fillColor.getAttributeLocation("a_position")
		gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVertexBuffer)
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)
		gl.enableVertexAttribArray(positionAttributeLocation)
		gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0)

		shaders.fillColor.setUniforms({
			foreColor: [1, 1, 1, 1],
		})

		gl.drawArrays(gl.LINE_LOOP, 0, vertices.length / 2)
	}

	drawMask(
		strokes: BrushStroke[],
		outputTexture: Texture,
		options: {
			isAdditive: boolean
			inputTexture?: Texture
			cache?: BrushMaskCacheEntry
			drawBounding?: boolean
			rect?: Rectangle
			aspectRatio?: number
		},
	) {
		const renderer = this.renderer
		const { gl, shaders } = renderer.backend
		const targetRect: Rectangle = options.rect ?? { x: 0, y: 0, width: 1, height: 1 }
		const aspectRatio = options.aspectRatio ?? outputTexture.height / outputTexture.width

		renderer.setRenderTarget(outputTexture)

		if (options.isAdditive === false) {
			gl.clearColor(0, 0, 0, 1)
			gl.clear(gl.COLOR_BUFFER_BIT)
		}

		const coordinatesAttributeLocation =
			shaders.brushMaskPainter.getAttributeLocation("coordinates")

		const localCoordAttribLocation =
			shaders.brushMaskPainter.getAttributeLocation("localCoordinates")

		const instancePositionAttribLocation =
			shaders.brushMaskPainter.getAttributeLocation("instancePosition")

		gl.enable(gl.BLEND)
		gl.blendEquation(gl.FUNC_ADD)
		gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA)

		for (let i = 0; i < strokes.length; i++) {
			const stroke = strokes[i]

			if (!stroke || stroke.points.length === 0) {
				continue
			}

			let points = stroke.points
			const cache = options.cache

			if (options.isAdditive === true && cache) {
				points = points.slice(cache.strokes[i]?.points.length ?? 0)

				if (points.length === 0) {
					continue
				}
			}

			// For healing, brush size does not increase with feather
			const brushSize = this.forHealing
				? stroke.size
				: stroke.size + stroke.feather * stroke.size
			const [quads, instancePositions] = this.createQuadVertices(
				points,
				brushSize,
				aspectRatio,
				targetRect,
			)

			gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVertexBuffer)
			gl.bufferData(gl.ARRAY_BUFFER, quads, gl.STATIC_DRAW)
			gl.vertexAttribPointer(coordinatesAttributeLocation, 2, gl.FLOAT, false, 0, 0)
			gl.enableVertexAttribArray(coordinatesAttributeLocation)

			gl.bindBuffer(gl.ARRAY_BUFFER, this.pointsBuffer)
			gl.bufferData(gl.ARRAY_BUFFER, instancePositions, gl.DYNAMIC_DRAW)
			gl.vertexAttribPointer(instancePositionAttribLocation, 2, gl.FLOAT, false, 0, 0)
			gl.enableVertexAttribArray(instancePositionAttribLocation)
			gl.vertexAttribDivisor(instancePositionAttribLocation, 1)

			gl.bindBuffer(gl.ARRAY_BUFFER, this.localCoordsBuffer)
			gl.bufferData(gl.ARRAY_BUFFER, this.localCoords, gl.STATIC_DRAW)
			gl.vertexAttribPointer(localCoordAttribLocation, 2, gl.FLOAT, false, 0, 0)
			gl.enableVertexAttribArray(localCoordAttribLocation)

			if (options.inputTexture && !this.forHealing) {
				options.inputTexture.bind(0)

				shaders.brushMaskPainter.setUniforms({
					inputTexture: 0,

					brushSize: stroke.size,
					flow: stroke.flow,
					feather: stroke.feather,
					density: stroke.density,

					erase: stroke.isErasing,
					colorAware: stroke.isColorAware,

					perspectiveMatrix: renderer.perspectiveMatrix,
				})
				shaders.brushMaskPainter.setUniforms(
					renderer.profileManager.getLensUniforms(renderer.photo),
				)
			} else {
				shaders.brushHealingMaskPainter.setUniforms({
					brushSize: stroke.size,
					flow: stroke.flow,
					feather: stroke.feather,
					density: stroke.density,
				})
			}

			gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, instancePositions.length / 2)
		}

		if (options.drawBounding === true && strokes.length > 0) {
			const rc = calcBoundingBox(strokes, outputTexture.height / outputTexture.width)
			const xMinMax: vec2 = [rc.x, rc.x + rc.width]
			const yMinMax: vec2 = [rc.y, rc.y + rc.height]

			this.drawRect(xMinMax, yMinMax)
		}

		gl.disable(gl.BLEND)
	}
}
